import { For, Match, Show, Switch, createContext, createEffect, createMemo, createSignal, onMount, useContext } from "solid-js";
import { k12IncidentSummary as api } from 'sis-app/api';
import { Button as STButton, STComponentWrapperProps, requireViewContext } from 'st-app'
import { SchoolIncidentSummaryData, StudentData, OtherPersonsData, ViolationData } from "sis-app/api";
import { RadioGroup as STRadioGroup } from "st-app";

import styles from './k12-incident-summary.module.css';

class K12IncidentSummaryContext {
    summary = createSignal<SchoolIncidentSummaryData>()

    loadAllViolations(): Record<string, ViolationData> {
        const summary = this.summary[0]();

        let allViolations = {};
        if (summary) {
            allViolations = summary.violations.reduce((obj, it) => {
                if (it.lookupValue) {
                    obj[it.lookupValue] = it;
                }

                return obj;
            }, {} as Record<string, ViolationData>);
        }

        return allViolations;
    }
}

type IncidentGroupBy = 'violation' | 'student';
class K12IncidentSummaryProps {
    context?: K12IncidentSummaryContext;
    incidentGU = '';
}

const k12IncidentSummaryContext = createContext(new K12IncidentSummaryContext());

export const useK12IncidentSummaryContext = () => useContext(k12IncidentSummaryContext);

const [groupBy, setGroupBy] = createSignal<IncidentGroupBy>('student');

export default function K12IncidentSummary(props: K12IncidentSummaryProps) {
    const context = createMemo(() => props.context || new K12IncidentSummaryContext());

    return <k12IncidentSummaryContext.Provider value={context()}>
        <IncidentSummaryInner {...props} />
    </k12IncidentSummaryContext.Provider>
}

function IncidentSummaryInner(props: K12IncidentSummaryProps) {
    const WIZARD_VIEW_GUID = 'AB3D8DA0-19AC-4487-9A89-B9B4EA214599';
    const viewContext = requireViewContext();
    const ctx = useK12IncidentSummaryContext();
    const [summary, setSummary] = ctx.summary;
    const incidentGU = createMemo(() => props.incidentGU || viewContext.identity());

    const showEditButton = !document.querySelector('#PrimaryView')?.classList.contains('VM_Add')
        && document.querySelector('#PrimaryView')?.getAttribute('data-guid') !== WIZARD_VIEW_GUID
        && !document.body.classList.contains('OverlayWindow');

    const onRefresh = () => {
        if (incidentGU() || incidentGU() != undefined) {
            api.LoadSummary(incidentGU(), viewContext.viewId())
                .then(summary => {
                    setSummary(summary);
                });
        }
        else {
            setSummary(undefined);
        }
    };

    createEffect(onRefresh);

    onMount(function () {
        if (viewContext.events) {
            viewContext.events.refresh = onRefresh;
        }
    });

    function openView() {
        if (window.ST.OpenOverlay) {
            const viewID = 'AB3D8DA0-19AC-4487-9A89-B9B4EA214599';
            const viewData = requireViewContext();

            const request: any = {
                REV_RENDER: {
                    '@VIEW': viewID,
                    VIEW: {
                        '@GUID': viewID,
                        '@RENDER_VIEW': 'YES',
                        REV_DATA_ROOT: {
                            '@ACTION': 'CURRENT',
                            REV_DATA_REQUEST: {
                                REV_VIEW: {
                                    '@GUID': viewID
                                }
                            },
                            IDENTITY: {
                                REV_ELEMENT: incidentGU()
                            }
                        }
                    },
                    'PASS_THROUGH': viewData && {
                        '@ParentViewGU': viewData.viewId(),
                        '@ParentViewClientID': viewData.primaryObjectGU()
                    }
                }
            };

            window.ST.OpenOverlay(undefined, request);
            //window.ST.OpenViewInDialog(viewID, incidentGU);
        }
    }

    return <div class="flexbox vertical" style="width: 100%">
        <Show when={summary()?.violations.length ?? 0 > 0}>
            <div class="flexbox horizontal padded">
                <Show when={showEditButton && summary()?.enableWizardViews && summary()?.enableEditButton}>
                    <div><STButton onClick={openView}>Edit</STButton></div>
                </Show>
                <div class="auto"></div>
                <div>
                    <STRadioGroup name="groupBy" value={[groupBy, setGroupBy]}>
                        <STRadioGroup.Option value={'student'} label="Group By Student"></STRadioGroup.Option>
                        <STRadioGroup.Option value={'violation'} label="Group By Violation"></STRadioGroup.Option>
                    </STRadioGroup>
                </div>
            </div>
            <div class={`${styles.group} flexbox vertical`}>
                <h2>
                    Incident {summary()?.incidentID}
                </h2>
                <div class="flexbox horizontal padded">
                    <span>{summary()!.incidentDate}</span>
                    <span>{summary()!.incidentTime}</span>
                </div>
                <i class={styles.description}>{summary()!.description}</i>
            </div>
            <Switch>
                <Match when={groupBy() == 'violation'}>
                    <IncidentViolations />
                </Match>

                <Match when={groupBy() == 'student'}>
                    <IncidentStudents />
                    <IncidentOtherParticipants />
                </Match>
            </Switch>
        </Show>
    </div>
}

K12IncidentSummary.defaultProps = {
    ...new K12IncidentSummaryProps(),
    ...new STComponentWrapperProps(),
}

class ViolationStudents {
    violation: ViolationData
    students: StudentData[] = []

    constructor(violation: ViolationData) {
        this.violation = violation;
    }
}

function IncidentViolations() {
    const ctx = useK12IncidentSummaryContext();

    // Reform the data structure with violations at the root level
    const violations = createMemo(() => {
        const res: Record<string, ViolationStudents> = {};
        const summary = ctx.summary[0]();
        const allViolations = ctx.loadAllViolations();

        if (summary?.students) {
            for (const stu of summary.students.filter(s => s.incidentRoleCode === '1')) {
                for (const viol of stu.violations) {
                    const violation = allViolations[viol];
                    if (violation) {
                        res[viol] = res[viol] ?? new ViolationStudents(violation);
                        res[viol].students.push(stu);
                    }
                }
            }
        }

        return Object.values(res);
    });

    return <div class="flexbox horizontal" style="flex-wrap: wrap">
        <For each={violations()}>{(viol) =>
            <div class={`${styles.group} auto`}>
                <h2>
                    <span class={styles.label}>Violation:</span>
                    {viol.violation.lookupDesc}
                </h2>

                <For each={viol.students}>{(stu, idx) =>
                    <div class={styles.group}>
                        <StudentCard student={stu} />
                        <StudentDiscipline student={stu} showViolations={false} suppressDivider={idx() === viol.students.length - 1} />
                    </div>
                }</For>
            </div>
        }</For>
    </div>
}

function IncidentStudents() {
    const ctx = useK12IncidentSummaryContext();
    const students = createMemo(() => {
        return ctx.summary[0]()?.students.filter(s => s.incidentRoleCode === '1') || [];
    });
    function getViolationsDesc(stu: StudentData) {
        const allViolations = ctx.loadAllViolations();

        return stu.violations
            .map(v => allViolations[v]?.lookupDesc)
            .filter(v => !!v)
            .join('; ')
    }

    return <div class="flexbox horizontal" style="flex-wrap: wrap">
        <For each={students()}>{(stu, idx) =>
            <div class={`${styles.group} auto`}>
                <StudentCard student={stu} violation={getViolationsDesc(stu)} />
                <StudentDiscipline student={stu} showViolations={true} suppressDivider={true} />
            </div>
        }</For>
    </div>
}

function StudentCard(props: { student: StudentData, violation?: string | undefined }) {
    const stu = props.student;

    return <div class="flexbox horizontal middle padded">
        <img class={styles.photo} src={stu.photo} alt={`Photo for ${stu.formattedName}`} />
        <div class="flexbox vertical">
            <h3>{stu.formattedName}</h3>
            <Show when={props.violation}>
                <span>{props.violation}</span>
            </Show>
        </div>
    </div>
}

function StudentDiscipline(props: { student: StudentData, showViolations: boolean, suppressDivider: boolean }) {
    const stu = props.student;

    return <div>
        <Show when={stu.interventions.length > 0}>
            <h3>Interventions</h3>
            <For each={stu.interventions}>{(iv) =>
                <div class={styles.group}>
                    <div class={styles.value}>{iv.intervention}</div>
                    <div class={styles.description}>{iv.comment}</div>
                </div>
            }</For>
        </Show>

        <Show when={stu.dispositions.length > 0}>
            <h3>Dispositions</h3>
            <div class="layout-table">
                <For each={stu.dispositions}>{disp =>
                    <div class="layout-row">
                        <h4>{disp.disposition}</h4>
                        <div style="padding-bottom: 10px">
                            <Show when={props.showViolations}>
                                <div>
                                    <span class={styles.label}>Violation:</span>
                                    <span class={styles.value}>{disp.violationDesc}</span>
                                </div>
                            </Show>
                            <div class="flexbox horizontal auto">
                                <span>
                                    <span class={styles.label}>Date:</span>
                                    <span class={styles.value}>{disp.dispositionDate}</span>
                                </span>
                                <Show when={disp.dispositionHours}>
                                    <span>
                                        <span class={styles.label}>Hours:</span>
                                        <span class={styles.value}>{disp.dispositionHours}</span>
                                    </span>
                                </Show>
                                <Show when={disp.dispositionDays}>
                                    <span>
                                        <span class={styles.label}>Days:</span>
                                        <span class={styles.value}>{disp.dispositionDays}</span>
                                    </span>
                                </Show>
                            </div>
                            <i>{disp.dispositionComment}</i>
                        </div>
                    </div>
                }</For>
            </div>
        </Show>

        <Show when={!props.suppressDivider}>
            <hr />
        </Show>
    </div>
}

function IncidentOtherParticipants() {
    const ctx = useK12IncidentSummaryContext();
    const others = createMemo(() => {
        return ctx.summary[0]()?.otherPersons.filter(o => o.incidentRoleCode === '1') || [];
    });
    function getViolationDesc(oth: OtherPersonsData) {
        const allViolations = ctx.loadAllViolations();

        return oth.violations
            .map(v => allViolations[v]?.lookupDesc)
            .filter(v => !!v)
            .join('; ')
    }

    return <div class="flexbox horizontal" style="flex-wrap: wrap">
        <For each={others()}>{(oth, idx) =>
            <div class={`${styles.group} auto`}>
                <OtherParticipantCard otherPerson={oth} violation={getViolationDesc(oth)} />
                <OtherParticipantDiscipline otherPerson={oth} showViolations={true} suppressDivider={true} />
            </div>
        }</For>
    </div>
}
function OtherParticipantCard(props: { otherPerson: OtherPersonsData, violation?: string | undefined }) {
    const oth = props.otherPerson;

    return <div class="flexbox horizontal middle padded">
        <img class={styles.photo} src={oth.photo} alt={`Photo for ${oth.formattedName}`} />
        <div class="flexbox vertical">
            <h3>{oth.formattedName}</h3>
            <Show when={props.violation}>
                <span>{props.violation}</span>
            </Show>
        </div>
    </div>
}

function OtherParticipantDiscipline(props: { otherPerson: OtherPersonsData, showViolations: boolean, suppressDivider: boolean }) {
    const oth = props.otherPerson;

    return <div>
        <Show when={oth.dispositions.length > 0}>
            <h3>Dispositions</h3>
            <div class="layout-table">
                <For each={oth.dispositions}>{disp =>
                    <div class="layout-row">
                        <h4>{disp.disposition}</h4>
                        <div style="padding-bottom: 10px">
                            <Show when={props.showViolations}>
                                <div>
                                    <span class={styles.label}>Violation:</span>
                                    <span class={styles.value}>{disp.violationDesc}</span>
                                </div>
                            </Show>
                            <div class="flexbox horizontal auto">
                                <span>
                                    <span class={styles.label}>Date:</span>
                                    <span class={styles.value}>{disp.dispositionDate}</span>
                                </span>
                                <Show when={disp.dispositionHours}>
                                    <span>
                                        <span class={styles.label}>Hours:</span>
                                        <span class={styles.value}>{disp.dispositionHours}</span>
                                    </span>
                                </Show>
                                <Show when={disp.dispositionDays}>
                                    <span>
                                        <span class={styles.label}>Days:</span>
                                        <span class={styles.value}>{disp.dispositionDays}</span>
                                    </span>
                                </Show>
                            </div>
                            <i>{disp.dispositionComment}</i>
                        </div>
                    </div>
                }</For>
            </div>
        </Show>

        <Show when={!props.suppressDivider}>
            <hr />
        </Show>
    </div>
}
